/**
 * @fileoverview Production progress bar chart widget — shows item counts
 * by status (Batched, Decorated, Binned, Shipped). Uses the
 * get_production_summary RPC for consistent data with the batches page.
 */

"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

interface ProductionSummary {
  batched: number;
  decorated: number;
  binned: number;
  shipped: number;
  total: number;
}

interface ProductionProgressProps {
  accountId?: string | null;
}

/**
 * @description Production progress bar chart matching the batches page widget.
 * Shows Batched → Decorated → Binned → Shipped columns.
 */
export function ProductionProgress({ accountId }: ProductionProgressProps) {
  const [summary, setSummary] = useState<ProductionSummary>({
    batched: 0,
    decorated: 0,
    binned: 0,
    shipped: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductionSummary();
  }, [accountId]);

  const fetchProductionSummary = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_production_summary", {
        p_from_date: null as any,
        p_to_date: null as any,
        p_account_id: (accountId || null) as any,
      });

      if (error) {
        console.error("Error fetching production summary:", error);
        return;
      }

      if (data) {
        const row = Array.isArray(data) ? data[0] : data;
        const batched = row?.batched || 0;
        const decorated = row?.decorated || 0;
        const binned = row?.binned || 0;
        const shipped = row?.shipped || 0;

        setSummary({
          batched,
          decorated,
          binned,
          shipped,
          total: batched + decorated + binned + shipped,
        });
      }
    } catch (error) {
      console.error("Error fetching production summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const maxVal = Math.max(
    summary.batched,
    summary.decorated,
    summary.binned,
    summary.shipped,
    1,
  );

  const barHeight = (value: number) =>
    `${Math.max(20, (value / maxVal) * 100)}%`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Production Progress</CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          {loading
            ? "Loading production data..."
            : `Summary of ${summary.total} items across all batches`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-2" />
            <span className="text-xs sm:text-sm text-slate-500">Loading production progress...</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:gap-3 h-48 sm:h-64">
            {/* Cyan - Batched */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-cyan-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{ height: barHeight(summary.batched) }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                  {summary.batched}
                </span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-cyan-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-cyan-700 font-medium">Batched</span>
              </div>
            </div>

            {/* Fuchsia - Decorated */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-fuchsia-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{ height: barHeight(summary.decorated) }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                  {summary.decorated}
                </span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-fuchsia-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-fuchsia-700 font-medium">Decorated</span>
              </div>
            </div>

            {/* Yellow - Binned */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-yellow-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{ height: barHeight(summary.binned) }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                  {summary.binned}
                </span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-yellow-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-yellow-700 font-medium">Binned</span>
              </div>
            </div>

            {/* Slate - Shipped */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-slate-900 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{ height: barHeight(summary.shipped) }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                  {summary.shipped}
                </span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-slate-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-slate-900 font-medium">Shipped</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
